import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useEffect, useRef, useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { DATE_FORMAT } from './format';

dayjs.extend(customParseFormat);

export type DateInputMode = 'date' | 'datetime';

/** What the user sees and types. The browser's native date controls follow its locale (mm/dd/yyyy for
 *  en-US), so the visible control is a text input; the native control is only used for its calendar popup. */
const SHOWN: Record<DateInputMode, string> = { date: DATE_FORMAT, datetime: `${DATE_FORMAT} hh:mm A` };
/** What the value is: the same strings <input type="date"> / <input type="datetime-local"> use. */
const ISO: Record<DateInputMode, string> = { date: 'YYYY-MM-DD', datetime: 'YYYY-MM-DDTHH:mm' };
const ACCEPTED: Record<DateInputMode, string[]> = {
  date: ['DD-MM-YYYY', 'D-M-YYYY'],
  datetime: ['DD-MM-YYYY hh:mm A', 'D-M-YYYY h:mm A', 'DD-MM-YYYY HH:mm', 'D-M-YYYY H:mm'],
};
const PLACEHOLDER: Record<DateInputMode, string> = { date: 'DD-MM-YYYY', datetime: 'DD-MM-YYYY hh:mm AM' };

export const isoToText = (iso: string, mode: DateInputMode): string => {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format(SHOWN[mode]) : '';
};

/** Typed text -> ISO value, or null when it is not a complete, real date (31-02-2026 is rejected). */
export const textToIso = (text: string, mode: DateInputMode): string | null => {
  const t = text.trim().replace(/[/.]/g, '-').replace(/\s+/g, ' ').toUpperCase();
  const d = dayjs(t, ACCEPTED[mode], true);
  return d.isValid() ? d.format(ISO[mode]) : null;
};

/** Inserts the separators as the user types digits ("0910" -> "09-10-"), and collapses ones they type themselves. */
const mask = (raw: string, grew: boolean, mode: DateInputMode): string => {
  let t = raw.replace(/-{2,}/g, '-').replace(/ {2,}/g, ' ').replace(/:{2,}/g, ':');
  if (grew && /^(\d{2}|\d{2}-\d{2})$/.test(t)) t += '-';
  else if (grew && mode === 'datetime' && /^\d{2}-\d{2}-\d{4}$/.test(t)) t += ' ';
  else if (grew && /^\d{2}-\d{2}-\d{4} \d{2}$/.test(t)) t += ':';
  else if (grew && /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}$/.test(t)) t += ' ';
  return t;
};

interface Props {
  value: string;
  onChange: (iso: string) => void;
  mode: DateInputMode;
  id?: string;
  'aria-label'?: string;
  'aria-required'?: boolean;
  disabled?: boolean;
  isInvalid?: boolean;
  size?: 'sm';
  min?: string;
  max?: string;
  style?: React.CSSProperties;
}

/** A DD-MM-YYYY [hh:mm AM/PM] text input with a calendar button. Emits '' until the text is a valid date. */
export function DateInput({ value, onChange, mode, id, disabled, isInvalid, size, min, max, style, ...aria }: Props) {
  const [text, setText] = useState(() => isoToText(value, mode));
  const [bad, setBad] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  // Follow outside changes (form reset, a preset, the calendar popup) but not our own partial typing,
  // which emits '' for text that isn't a date yet.
  useEffect(() => {
    if (value !== (textToIso(text, mode) ?? '')) {
      setText(isoToText(value, mode));
      setBad(false);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (next: string) => {
    setText(next);
    setBad(false);
    onChange(next.trim() === '' ? '' : (textToIso(next, mode) ?? ''));
  };

  const openCalendar = () => {
    const el = picker.current;
    if (!el || disabled) return;
    try {
      el.showPicker();
    } catch {
      el.focus();
    }
  };

  return (
    <InputGroup size={size} className="position-relative flex-nowrap" style={style}>
      <Form.Control
        {...aria}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={PLACEHOLDER[mode]}
        value={text}
        disabled={disabled}
        isInvalid={isInvalid || bad}
        onChange={(e) => commit(mask(e.target.value, e.target.value.length > text.length, mode))}
        onBlur={() => setBad(text.trim() !== '' && textToIso(text, mode) === null)}
      />
      <button type="button" className="btn btn-outline-secondary" aria-label="Open calendar" disabled={disabled} onClick={openCalendar}>
        <i className="bi bi-calendar3" aria-hidden />
      </button>
      {/* Only supplies the calendar popup; its value is read back as the ISO string we use anyway. */}
      <input
        ref={picker}
        type={mode === 'date' ? 'date' : 'datetime-local'}
        value={value}
        min={min}
        max={max}
        tabIndex={-1}
        aria-hidden
        onChange={(e) => e.target.value && commit(isoToText(e.target.value, mode))}
        style={{ position: 'absolute', left: 0, bottom: 0, width: '100%', height: 0, opacity: 0, pointerEvents: 'none', border: 0, padding: 0 }}
      />
    </InputGroup>
  );
}
